import { entityEffects, selectCurrentEntity, selectEntityDisplayName } from '@opengov/app-core/entity';
import { getEntity, getEntitySubdomain, getNumericalEntityId } from '@opengov/app-core/entity/selectors';
import { coreFeatureFlagEffects } from '@opengov/app-core/featureFlag';
import { isOpenGovAdmin, userSelector } from '@opengov/app-core/session/selectors';
import { modalActionCreators } from '@opengov/component-library-classic/module/modals';
import { getLocation } from '@opengov/compose-app-router/selectors';
import { ensureAll } from '@opengov/compose-app-router/onEnter';
import { routerActions } from '@opengov/compose-app-router/state';
import { createSelector, createStructuredSelector } from '@opengov/compose-app/reselect';
import { selectFeatureFlags } from '@opengov/feature-flag/selectors';
import { IEntity } from '@opengov/og-api-ts';
import { FLAG_PRODUCT_STORIES_ACCESS_CONTROL } from '@opengov/stories-shared/constants';
import { generatePageTitle } from '@opengov/stories-shared/utils';

import { storiesMsgs } from '../../locale';
import { onEnterStoriesRoutes } from '../../routes/onEnter';
import { maintenancePath, publicationListPath } from '../../routes/paths';
import { PublicationView, IPublicationsViewProps } from '../components/Publications/PublicationView/PublicationView/PublicationView';
import { CUSTOM_REPORTS, REPORTS, STORIES } from '../components/StoryList/Sidebar/Sidebar';
import {
  selectOnlinePublicationTranslatorFlag,
  selectStoriesBulkUpdateStoryStatusFeatureFlag,
  selectStoriesUpdateReportEmbedsFeatureFlag,
  selectIsCustomSpreadsheetReportEnabledFlag,
  selectIsFormAndWorkflowsFeatureFlagEnabled,
  selectIsPerformanceManagementFeatureFlagEnabled,
  selectIsNewReportTileModalEnabledFlag
} from '../common/flags';
import {
  navigateToPublicationFolderList,
  navigateToPublicationList,
  navigateToPublicationStoryView,
  navigateToPublicationView,
  navigateToSetupOnlinePublication,
  navigateToPublicationSetupPDF
} from '../state/navigation';
import {
  // selectPublicationFoldersTree, selectPublicationFolderTree, selectFolderStructure and
  // selectStoryFolders removed - the component fetches via hooks instead.
  selectShowFollowableStories,
  selectShowGlobalNavBar,
  selectStoriesOrganizationFlag,
  selectStoryMarginsEnabled
} from '../selectors/storyListSelectors';
import { selectLastPath } from '../selectors/storiesSelectors';

type NextLocationParams<Params> = {
  params: Params;
};

type PublicationViewRouteNextLocation = NextLocationParams<{
  entitySubdomain: string;
  publicationId: string;
  sectionId: string;
  copyPublicationJobId: string;
}>;

type OnError = (message: string, action: unknown) => void;

// Slim publicationViewProps inlined into this route file.
// Server data and mutations removed — PublicationView fetches via hooks.
// Route passes permissions, route params, flags, messages, and navigation only.
const buildPublicationViewRouteProps = createStructuredSelector<any, IPublicationsViewProps>({
  showGlobalNavBar: selectShowGlobalNavBar,
  entityId: getNumericalEntityId,
  isOpenGovAdmin,
  entityName: createSelector(getEntity, (entity) => entity.display_name),
  entitySubdomain: getEntitySubdomain,
  publicationId: createSelector(getLocation, (location) => location.params?.publicationId ?? ''),
  sectionId: createSelector(getLocation, (location) => location.params?.sectionId ?? ''),
  // publicationFolders + publicationFoldersTree removed — component uses usePublicationFolderStructure hook
  // folderStructure + rootFolderId removed — component uses useStoryFolders hook
  messages: createSelector(storiesMsgs, (msgs) => ({ ...msgs.publicationView })),
  subjectClaimsMessages: createSelector(storiesMsgs, (msgs) => ({ ...msgs.subjectClaims })),
  canOrganizeStories: selectStoriesOrganizationFlag,
  user: userSelector,
  lastPath: selectLastPath,
  navigateToPublicationList: createSelector(
    getEntitySubdomain,
    (entitySubdomain) => () => navigateToPublicationList(entitySubdomain)
  ),
  navigateToPublicationSectionList: createSelector(
    getEntitySubdomain,
    (entitySubdomain) => (publicationId, sectionId) =>
      navigateToPublicationFolderList(entitySubdomain, publicationId, sectionId)
  ),
  navigateToPublicationStoriesList: createSelector(
    getEntitySubdomain,
    (entitySubdomain) => (publicationId) => navigateToPublicationView(entitySubdomain, publicationId)
  ),
  navigateToPublicationStoryView: createSelector(
    getEntitySubdomain,
    (entitySubdomain) => (storyId, isTemplate, publicationId) =>
      navigateToPublicationStoryView(entitySubdomain, storyId, isTemplate, publicationId)
  ),
  navigateToSetupOnlinePublication: createSelector(
    getEntitySubdomain,
    (entitySubdomain) => (publicationId) => navigateToSetupOnlinePublication(entitySubdomain, publicationId)
  ),
  navigateToPublicationSetupPDF: createSelector(
    getEntitySubdomain,
    (entitySubdomain) => (publicationId) => navigateToPublicationSetupPDF(entitySubdomain, publicationId)
  ),
  storyMessages: createSelector(storiesMsgs, (msgs) => ({ ...msgs.storyList })),
  publicationModalMessages: createSelector(storiesMsgs, (msgs) => ({ ...msgs.publicationModals })),
  modalMessages: createSelector(storiesMsgs, (msgs) => ({ ...msgs.modals })),
  storyMarginsEnabled: selectStoryMarginsEnabled,
  isReportEmbedListSelected: createSelector(getLocation, (location) => {
    const path = location.pathname;
    return path.includes('/embedded-reports');
  }),
  displayReportEmbeds: selectStoriesUpdateReportEmbedsFeatureFlag,
  openModal: () => (modal) => modalActionCreators.setModalComponentType(modal),
  isPublicationCustomCssEnabled: createSelector(selectFeatureFlags, (flags) =>
    flags.get('product-stories-feature-custom-css-code')
  ),
  isPublicationSetupPdfEnabled: createSelector(selectFeatureFlags, (flags) =>
    flags.get('product-feature-publications-setup-pdf')
  ),
  copyPublicationJobId: createSelector(getLocation, (location) => location.params?.copyPublicationJobId ?? ''),
  selectedSectionLabel: createSelector(getLocation, (location) => {
    const path = location.pathname;
    return path.includes('/custom-reports')
      ? CUSTOM_REPORTS
      : path.includes('/embedded-reports')
        ? REPORTS
        : STORIES;
  }),
  entityUuid: createSelector(getEntity, (entity) => entity.uuid),
  isBulkUpdateStoryStatusEnabled: selectStoriesBulkUpdateStoryStatusFeatureFlag,
  enableGoogleTranslateForPublications: selectOnlinePublicationTranslatorFlag,
  storiesAccessControlEnabled: createSelector(selectFeatureFlags, (flags) =>
    flags.get(FLAG_PRODUCT_STORIES_ACCESS_CONTROL)
  ),
  canFollowStories: selectShowFollowableStories,
  isCustomSpreadsheetReportEnabled: selectIsCustomSpreadsheetReportEnabledFlag,
  isFormsAndWorkflowsFeatureEnabled: selectIsFormAndWorkflowsFeatureFlagEnabled,
  isPerformanceManagementFeatureEnabled: selectIsPerformanceManagementFeatureFlagEnabled,
  isNewReportTileModalEnabled: selectIsNewReportTileModalEnabledFlag
});

export const publicationViewRoute = {
  name: 'PublicationView',
  title: createSelector(selectEntityDisplayName, (name) => generatePageTitle('Publication', name)),
  selector: buildPublicationViewRouteProps,
  component: PublicationView,
  onEnter: ensureAll([
    onEnterStoriesRoutes,
    async (state, nextLocation: PublicationViewRouteNextLocation, onSuccess, onError: OnError) => {
      const { entitySubdomain } = nextLocation.params;
      // The component reads sectionId/copyPublicationJobId directly from route params via selector.
      await entityEffects
        .fetchRequestedOrCurrentEntity({
          requestedEntitySubdomain: entitySubdomain,
          currentEntity: selectCurrentEntity(state)
        })
        .then(async (entity: IEntity) => {
          if (entity.subdomain !== entitySubdomain) {
            return onError(
              'Entity not found...',
              routerActions.replace.create({ pathname: publicationListPath(entity.subdomain) })
            );
          }
          await coreFeatureFlagEffects.getFlags().then(async (flags) => {
            if (flags && flags['product-stories-maintenance-window']) {
              return onError(
                'Redirecting to maintenance page...',
                routerActions.replace.create({ pathname: maintenancePath(entitySubdomain) })
              );
            }

            onSuccess();
            // The component derives selectedSectionLabel from route name (STORIES vs REPORTS vs CUSTOM_REPORTS).
            // Subject claims are fetched in PublicationView via useCurrentSubjectClaims /
            // useSubjectPublicationStoryClaim.
          });
        });
    }
  ])
};

type PublicationReportEmbedRouteNextLocation = NextLocationParams<{
  entitySubdomain: string;
  publicationId: string;
  copyPublicationJobId: string;
}>;

export const publicationReportEmbedRoute = {
  name: 'PublicationView',
  title: createSelector(selectEntityDisplayName, (name) => generatePageTitle('Publication', name)),
  selector: buildPublicationViewRouteProps,
  component: PublicationView,
  onEnter: ensureAll([
    onEnterStoriesRoutes,
    async (state, nextLocation: PublicationReportEmbedRouteNextLocation, onSuccess, onError: OnError) => {
      const { entitySubdomain, publicationId: _publicationId } = nextLocation.params;
      await entityEffects
        .fetchRequestedOrCurrentEntity({
          requestedEntitySubdomain: entitySubdomain,
          currentEntity: selectCurrentEntity(state)
        })
        .then(async (entity: IEntity) => {
          if (entity.subdomain !== entitySubdomain) {
            return onError(
              'Entity not found...',
              routerActions.replace.create({ pathname: publicationListPath(entity.subdomain) })
            );
          }
          await coreFeatureFlagEffects.getFlags().then(async (flags) => {
            if (flags && flags['product-stories-maintenance-window']) {
              return onError(
                'Redirecting to maintenance page...',
                routerActions.replace.create({ pathname: maintenancePath(entitySubdomain) })
              );
            }

            onSuccess();
          });
        });
    }
  ])
};

type PublicationTemplateListRouteNextLocation = NextLocationParams<{
  entitySubdomain: string;
  publicationId: string;
}>;

export const publicationTemplateListRoute = {
  name: 'PublicationTemplateList',
  title: createSelector(selectEntityDisplayName, (name) => generatePageTitle('Template', name)),
  selector: buildPublicationViewRouteProps,
  component: PublicationView,
  onEnter: ensureAll([
    onEnterStoriesRoutes,
    async (state, nextLocation: PublicationTemplateListRouteNextLocation, onSuccess, onError: OnError) => {
      const { entitySubdomain } = nextLocation.params;
      await entityEffects
        .fetchRequestedOrCurrentEntity({
          requestedEntitySubdomain: entitySubdomain,
          currentEntity: selectCurrentEntity(state)
        })
        .then(async (entity: IEntity) => {
          if (entity.subdomain !== entitySubdomain) {
            return onError(
              'Entity not found...',
              routerActions.replace.create({ pathname: publicationListPath(entity.subdomain) })
            );
          }
          await coreFeatureFlagEffects.getFlags().then(async (flags) => {
            if (flags && flags['product-stories-maintenance-window']) {
              return onError(
                'Redirecting to maintenance page...',
                routerActions.replace.create({ pathname: maintenancePath(entitySubdomain) })
              );
            }

            onSuccess();
          });
        });
    }
  ])
};

export const publicationCustomReportRoute = {
  name: 'PublicationCustomReports',
  title: createSelector(selectEntityDisplayName, (name) => generatePageTitle('Publication', name)),
  component: PublicationView,
  selector: buildPublicationViewRouteProps,
  onEnter: ensureAll([
    onEnterStoriesRoutes,
    async (
      state,
      nextLocation: NextLocationParams<{ entitySubdomain: string; publicationId: string }>,
      onSuccess,
      onError: OnError
    ) => {
      const { entitySubdomain, publicationId: _publicationId2 } = nextLocation.params;
      await entityEffects
        .fetchRequestedOrCurrentEntity({
          requestedEntitySubdomain: entitySubdomain,
          currentEntity: selectCurrentEntity(state)
        })
        .then(async (entity: IEntity) => {
          if (entity.subdomain !== entitySubdomain) {
            return onError(
              'Entity not found...',
              routerActions.replace.create({ pathname: publicationListPath(entity.subdomain) })
            );
          }

          await coreFeatureFlagEffects.getFlags().then(async (flags) => {
            if (flags && flags['product-stories-maintenance-window']) {
              return onError(
                'Redirecting to maintenance page...',
                routerActions.replace.create({ pathname: maintenancePath(entitySubdomain) })
              );
            }

            onSuccess();
          });
        });
    }
  ])
};
